import { createSequence } from '@olivierpascal/helpers';

import type { IContainerProps } from '~/components/utils/Container';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { ErrorMessage } from '~/components/molecules/ErrorMessage';
import { Pagination } from '~/components/molecules/Pagination';
import {
  PollVoteListItem,
  PollVoteListItemSkeleton,
} from '~/components/molecules/PollVoteListItem';
import { Table } from '~/components/molecules/Table';
import { usePagination } from '~/hooks/usePagination';
import { usePollResultsQuery } from '~/lib/api/modules/Polls/hooks/usePollResultsQuery';

const DEFAULT_SKELETON_COUNT = 4;

export type IPollVoteListProps = IContainerProps & {
  appId: string;
  pollId: string;
};

export const PollVoteList: React.FC<IPollVoteListProps> = (props) => {
  const { sx, appId, pollId } = props;
  const pagination = usePagination();
  const pollResultsQuery = usePollResultsQuery(
    {
      appId,
      pollId,
      body: {
        start: pagination.start,
        limit: pagination.limit,
      },
    },
    {
      enabled:
        Number.isInteger(pagination.start) &&
        Number.isInteger(pagination.limit),
      keepPreviousData: true,
    },
  );

  const pollResults = pollResultsQuery.data;
  const isEmpty = !pollResultsQuery.isLoading && !pollResults?.votes?.length;

  if (isEmpty) {
    return null;
  }

  return (
    <ContentBlock sx={sx}>
      {pollResultsQuery.isError ? (
        <ContentBlock.Body first>
          <ErrorMessage error={pollResultsQuery.error} />
        </ContentBlock.Body>
      ) : (
        <>
          <Table
            gridTemplateColumns={`repeat(${1 + (pollResults?.poll?.options?.length ?? 0)}, 1fr)`}
          >
            {pollResultsQuery.isLoading || pollResultsQuery.isPreviousData ? (
              createSequence(DEFAULT_SKELETON_COUNT).map((index) => (
                <PollVoteListItemSkeleton key={index} first={index === 0} />
              ))
            ) : (
              <>
                <Table.Row first>
                  <Table.Header />
                  {pollResults?.poll?.options?.map((option) => (
                    <Table.Header key={option.id}>{option.text}</Table.Header>
                  ))}
                </Table.Row>

                {pollResults?.votes?.map(
                  (pollVote) =>
                    pollResults.poll && (
                      <PollVoteListItem
                        key={pollVote._id}
                        poll={pollResults.poll}
                        vote={pollVote}
                      />
                    ),
                )}
              </>
            )}
          </Table>

          <ContentBlock.Footer>
            <Pagination
              pagination={pagination}
              total={pollResultsQuery.data?.totalVotes}
            />
          </ContentBlock.Footer>
        </>
      )}
    </ContentBlock>
  );
};
